// src/lib/windgram/renderer.js
//
// WindgramRenderer — a React-free, i18n-free imperative D3 renderer that assembles
// the windgram lib modules into the full atmospheric cross-section chart.
//
// This is a hand-port of Skysight's `zn` class (docs/skysight-windgram-bundle.js,
// constructor 828-905, draw() 924-1620, changeAlt 906-908, changeVariable 909-914,
// updateBarbsVisibility 915-923, getCenteredBandConfig 1900, drawZeroIsotherm 1905,
// getFillColor 1949, drawLegend 1960, updateSVG/zoom 1681-1777). See
// docs/skysight-windgram-symbols.md for the symbol map.
//
// Input:  element (DOM node), data ({ data: [...timesteps...], base: "627.9" }),
//         variable (a TAB_ORDER key), units ({ vert, temp, spd, hgt }).
// Output: SVG drawn into `element`.

import { select, pointer } from 'd3-selection';
import { scaleLinear } from 'd3-scale';
import { axisBottom, axisLeft } from 'd3-axis';
import { geoPath, geoTransform } from 'd3-geo';
import { zoom as d3Zoom } from 'd3-zoom';

import { FIELDS, MARGIN, DEFAULT_ALT } from './constants';
import { makeScale, convertUnit, UNIT_LABELS } from './scales';
import { centeredBandConfig, getFillColor } from './bandConfig';
import { buildGrid } from './contours';
import { contours as d3Contours } from 'd3-contour';
import { buildAltitudeScale } from './projection';
import { barbPath, barbRotation } from './barbs';
import { isolines } from './isolines';
import {
  cumulusColumns,
  drawBoundaryLine,
  drawCondensationLine,
  drawCloudArea,
} from './overlays';
import { drawCumulus, drawRain, drawLightning } from './icons';
import { drawLegend } from './legend';

// Horizontal squeeze factor for contours/overlays/icons. Skysight's bundle
// (line 1386) squeezes by 0.33 (13 steps) / 0.68 (28 steps) because it lays its
// data out on a fixed ~39-column (3-day) canvas and compresses to fit. We don't
// replicate that fixed canvas — our contour geoTransform and overlay paths are
// already projected across the full inner width using the ACTUAL step count
// (the same way barbs are placed), so no extra squeeze is needed: gt = 1.
const HORIZONTAL_SQUEEZE = 1;

// Skysight $n() (bundle line 822): grid height = the smallest count of altitude
// slices ≤ alt across all timesteps. Reducing `alt` shrinks the grid.
function gridHeightForAlt(timeSteps, alt) {
  const counts = timeSteps
    .map((t) => t.data.filter((s) => s.altitude <= alt).length)
    .filter((n) => n > 0);
  return counts.length === 0 ? 0 : Math.min(...counts);
}

// Resolve the effective (opaque) background color behind an element by walking
// up the DOM until a non-transparent background-color is found. Used for the
// dark-theme cloud-table gridlines so they match the real bg shown in the gap
// below the table (rather than a guessed colour).
function resolveBackgroundColor(node) {
  if (typeof window === 'undefined' || !node || !window.getComputedStyle) return null;
  let el = node;
  while (el && el.nodeType === 1) {
    const bg = window.getComputedStyle(el).backgroundColor;
    if (bg && bg !== 'transparent' && !/rgba\(\s*0\s*,\s*0\s*,\s*0\s*,\s*0\s*\)/.test(bg)) {
      return bg;
    }
    el = el.parentElement;
  }
  return null;
}

// Pretty unit string for a field's legend title (mirrors bundle 1084-1090).
function prettyUnitFor(scaleKey, units) {
  switch (scaleKey) {
    case 'ti':
    case 'cldcover':
      return UNIT_LABELS[units.vert] || units.vert || 'm/s';
    case 'temp':
      return UNIT_LABELS[units.temp] || units.temp || '°C';
    case 'wind':
      return UNIT_LABELS[units.spd] || units.spd || 'm/s';
    case 'lapse':
      return '°C/1000ft';
    case 'cldfra':
    case 'rh':
      return '%';
    default:
      return '';
  }
}

export class WindgramRenderer {
  constructor(element, data, variable, units = {}, options = {}) {
    this.element = element;
    this.margin = { ...MARGIN };
    this.alt = DEFAULT_ALT;
    this.variable = variable;
    this.units = units || {};
    this.dataObj = data;
    // i18n strings threaded in from the React panel (English fallbacks below).
    this.i18n = (options && options.i18n) || {};

    // Time-steps: numeric strings are parseFloat'd at the boundary.
    this.timeSteps = (data.data || []).map((step) => ({
      ...step,
      dt: step.dt,
      pblh: parseFloat(step.pblh),
      zsfclcl: parseFloat(step.zsfclcl),
      cu_oktas: step.cu_oktas != null ? parseFloat(step.cu_oktas) : 0,
      accrain: step.accrain != null ? parseFloat(step.accrain) : 0,
      cape: step.cape != null ? parseFloat(step.cape) : 0,
      data: step.data.map((slice) => ({
        ...slice,
        altitude: parseFloat(slice.altitude),
      })),
    }));

    this.base = parseFloat(data.base);
    this.loAlt = this.timeSteps.length
      ? parseFloat(this.timeSteps[0].data[0].altitude)
      : 0;

    // Build all 7 colour scales keyed by scaleKey (mirrors constructor 890-896).
    this.tiScale = makeScale('ti', this.units);
    this.lapseScale = makeScale('lapse', this.units);
    this.cldfraScale = makeScale('cldfra', this.units);
    this.rhScale = makeScale('rh', this.units);
    this.tempScale = makeScale('temp', this.units);
    this.cldcoverScale = makeScale('cldcover', this.units);
    this.windScale = makeScale('wind', this.units);

    // maxPBLH over the first 13 steps (constructor 901-903).
    this.maxPBLH = this.timeSteps.length
      ? Math.max(...this.timeSteps.slice(0, 13).map((t) => t.pblh + 300))
      : 0;
    // maxAltitude from the slice altitudes (constructor 904).
    this.maxAltitude = this.timeSteps.length
      ? Math.max(...this.timeSteps[0].data.map((s) => s.altitude))
      : 0;

    this.tempGrid = [];
    this.contourGridWidth = 0;
    this.contourGridHeight = 0;
    this.transformK = 1;
  }

  scaleForKey(scaleKey) {
    return this[`${scaleKey}Scale`] || makeScale(scaleKey, this.units);
  }

  // --- public API (bundle 906-923) ------------------------------------------

  // Update the translated-strings object without rebuilding the renderer. The
  // panel calls this on language change, then draw() to repaint with new labels.
  setI18n(i18n) {
    this.i18n = i18n || {};
  }

  changeAlt(a) {
    this.alt = a;
    this.draw();
    this.updateBarbsVisibility();
  }

  changeVariable(v, alt) {
    if (this.variable !== v) {
      this.variable = v;
      if (alt !== undefined) {
        this.alt = alt;
      }
      this.draw();
    }
    this.updateBarbsVisibility();
  }

  // Barbs visible when the active field has barbs (overview/wind/cldfra).
  // The bundle gates on variable === 'cldcover' || 'wind' || isOverview; in our
  // FIELDS those are exactly the hasBarbs:true tabs.
  updateBarbsVisibility() {
    if (!this.svgDraw) return;
    const visible = FIELDS[this.variable] && FIELDS[this.variable].hasBarbs ? 1 : 0;
    this.svgDraw.selectAll('.barbs').style('opacity', visible);
  }

  // --- draw -----------------------------------------------------------------

  draw() {
    const el = this.element;
    el.innerHTML = '';

    // Theme detection (mirrors PointTableV2/WindgramChart). Stored on `this` so
    // the zoom handler + helper methods (axes, legends, tooltip) can read it.
    const isDark =
      typeof window !== 'undefined' &&
      localStorage.getItem('color-theme') === 'dark';
    this.isDark = isDark;
    // Theme-aware chrome colours (label text, axis lines).
    this.tickLabelColor = isDark ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.6)';
    this.axisLineColor = isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)';
    this.dateLabelColor = isDark ? 'rgba(255,255,255,0.85)' : '#1f2937';
    this.legendTextColor = isDark ? 'rgba(255,255,255,0.85)' : '#1f2937';
    // Cloud-table gridline colour: white on light theme; on dark theme use the
    // actual page/panel background (same colour shown in the gap below the table)
    // so the gridlines read as cut-outs instead of harsh white lines.
    this.bgColor = isDark ? (resolveBackgroundColor(el) || '#0f172a') : '#ffffff';

    const field = FIELDS[this.variable] || FIELDS.ti;
    const isOverview = !!field.isOverview || this.variable === 'cldcover';

    const innerWidth =
      el.clientWidth - this.margin.left - this.margin.right;
    const innerHeight =
      el.clientHeight - this.margin.top - this.margin.bottom;
    // Overscan canvas: lay all horizontal content across contentWidth (≈3 days
    // wide) and clip to the viewport (innerWidth). At zoom k=1 the viewport shows
    // ~1 day; zooming out to k=1/3 fits all 3 days (mirrors Skysight draw() 924-931
    // where this.width = (clientWidth - marginL - marginR) * 3.2).
    const contentWidth = innerWidth * 3.2;
    this.innerWidth = innerWidth;
    this.contentWidth = contentWidth;
    this.width = contentWidth;

    // Cloud-cover table band (ti tab only): reserve a 3-row strip ABOVE the plot
    // and shrink the plot area to fit (mirrors bundle 938-1051, `this.height -=
    // tableHeight`). On all other tabs TABLE_H = 0 and the layout is unchanged.
    const TABLE_ROW_H = 24; // Skysight row height (measured from live table)
    const TABLE_ROWS = 3;
    const TABLE_BOTTOM_GAP = 10; // breathing room between the table and the plot
    const isTi = this.variable === 'ti';
    // Cells occupy TABLE_ROW_H*TABLE_ROWS; the plot is shifted down by the full
    // TABLE_H, leaving TABLE_BOTTOM_GAP empty below the table (Skysight has a
    // visible margin there).
    const TABLE_H = isTi ? TABLE_ROW_H * TABLE_ROWS + TABLE_BOTTOM_GAP : 0;
    this.tableHeight = TABLE_H;
    // plotHeight is the reduced plot area; the plot content is shifted DOWN by
    // TABLE_H so it sits below the table band.
    const plotHeight = innerHeight - TABLE_H;
    this.height = plotHeight;

    const stepCount = this.timeSteps.length;
    if (stepCount === 0) return;

    // Grid height clipped to the active alt (bundle $n / line 1129).
    const gridHeight = gridHeightForAlt(this.timeSteps, this.alt);
    if (gridHeight === 0) return;
    // Clip each timestep's slices to the first `gridHeight` altitudes.
    const steps = this.timeSteps.map((t) => ({
      ...t,
      data: t.data.slice(0, gridHeight),
    }));
    const cols = steps.length;
    const rows = gridHeight;
    this.contourGridWidth = cols;
    this.contourGridHeight = rows;
    // Stored for the zoom handler so it can recompute barb-group x positions
    // (barbs are NOT in the scaled wgContent group; see attachZoom).
    this.cols = cols;

    // Altitude → pixel scale (full-range or split) (bundle 1221-1234).
    const { y, terrain } = buildAltitudeScale({
      innerHeight: plotHeight,
      base: this.base,
      alt: this.alt,
      maxPBLH: this.maxPBLH,
      splitAxis: field.splitAxis,
      units: this.units,
      loAlt: this.loAlt,
    });
    this.yScale = y;
    // The y-axis bottom is the projected terrain (base mapped onto the
    // [loAlt, alt] → [0, alt] scale), in display units — this is the value
    // Skysight shows as its base tick (e.g. "535m"), not the raw MSL base.
    this.terrain = terrain;

    // SVG sized to the full client box.
    const svg = select(el)
      .append('svg')
      .attr('class', 'mainSVG')
      .style('font-family', 'sans-serif')
      .attr('width', el.clientWidth)
      .attr('height', el.clientHeight);
    this.svg = svg;

    // Inner drawing group, translated by the left/top margins.
    const svgDraw = svg
      .append('g')
      .attr(
        'transform',
        `translate(${this.margin.left},${this.margin.top})`,
      );
    this.svgDraw = svgDraw;

    // Clip path: the viewport plot rect (0..innerWidth, 0..plotHeight). All
    // zoomed/scrolled content is clipped to this so the off-viewport days stay
    // hidden until you zoom out (bundle 1203-1211 + the 3.2× overscan canvas).
    svgDraw
      .append('defs')
      .append('clipPath')
      .attr('id', 'wg-clip-path')
      .append('rect')
      .attr('width', innerWidth)
      .attr('height', plotHeight);

    // Cloud-cover table band (ti only): a clipped group spanning the viewport
    // width, sitting in the reserved top strip (y 0..TABLE_H within svgDraw).
    // Its cells live in CONTENT coordinates so they align with the chart
    // columns, and it receives the SAME zoom transform as wgContent (see
    // attachZoom). Drawn before the plot group so it sits above visually.
    if (TABLE_H > 0) {
      svgDraw
        .append('defs')
        .append('clipPath')
        .attr('id', 'wg-table-clip')
        .append('rect')
        .attr('width', innerWidth)
        .attr('height', TABLE_H);
      const wgTableClip = svgDraw
        .append('g')
        .attr('clip-path', 'url(#wg-table-clip)');
      const wgTable = wgTableClip.append('g').attr('class', 'wg-table');
      this.wgTable = wgTable;
      this.drawCloudTable(wgTable, steps, contentWidth, cols, TABLE_ROW_H);
      // Row labels live in the static LEFT margin (NOT zoomed): just left of x=0.
      this.drawCloudTableLabels(svgDraw, TABLE_ROW_H);
    } else {
      this.wgTable = null;
    }

    // The clip must be on a STATIC wrapper, NOT on the zoomed group — otherwise
    // the clip region scales with the content and shrinks to the left third at
    // k<1, hiding days 2-3. So: static wgClip (clip-path, never transformed) →
    // inner wgContent (the zoom target, translate(tx,0) scale(k,1)). Shifted
    // DOWN by TABLE_H so the plot sits below the cloud-cover table band.
    const wgClip = svgDraw
      .append('g')
      .attr('transform', `translate(0,${TABLE_H})`)
      .attr('clip-path', 'url(#wg-clip-path)');

    // wgContent: the single group holding ALL horizontal content (contour fills,
    // barbs, overlays, icons, day dividers, date labels), origin at margin.left,
    // target of the zoom transform. Lives inside the static clipped wrapper.
    const wgContent = wgClip
      .append('g')
      .attr('class', 'wg-content');
    this.wgContent = wgContent;

    // contoursGroup keeps the historical .main_SVG class (used by tests + the
    // contour hover code). It is now a child of wgContent (so it zooms/scrolls).
    const contoursGroup = wgContent
      .append('g')
      .attr('class', 'main_SVG');
    this.contoursGroup = contoursGroup;

    // wg-barbs: barb groups live in a SEPARATE group that is a child of the
    // STATIC clipped wrapper (wgClip), NOT wgContent. This means barbs are
    // clipped to the viewport but are NOT affected by wgContent's scale(k,1) —
    // so the glyphs never get horizontally squished on zoom-out. The zoom
    // handler repositions each barb group by the zoom factor instead (see
    // attachZoom/applyTransform). It sits under svgDraw → wgClip so
    // updateBarbsVisibility's `svgDraw.selectAll('.barbs')` still finds them.
    const wgBarbs = wgClip
      .append('g')
      .attr('class', 'wg-barbs');
    this.wgBarbs = wgBarbs;

    const gt = HORIZONTAL_SQUEEZE;

    // geoTransform projection: grid (col,row) → pixels (bundle 1236-1241).
    // x = (col * contentWidth) / cols ; y = altScale(((rows - row) / rows) * alt)
    const altMeters = this.alt;
    const hgt = this.units.hgt;
    const projection = geoTransform({
      point(gx, gy) {
        const px = (gx * contentWidth) / cols;
        const altFraction = ((rows - gy) / rows) * altMeters;
        const py = y(convertUnit(altFraction, hgt));
        this.stream.point(px, py);
      },
    });
    this.projection = projection;
    const pathGen = geoPath(projection);

    // --- contour fills --------------------------------------------------------
    // Overview/cldcover draw the primary variable PLUS a cldfra overlay on top
    // (bundle drawContours 1824-1828). Section index i=0 primary, i=1 overlay.
    const sections = isOverview ? [this.variable, 'cldfra'] : [this.variable];

    // Hover tooltip (bundle 1857-1895): an absolutely-positioned div showing the
    // value of the contour band under the cursor + its unit. Lives inside the
    // (position:relative) chart element so it's cleared on every redraw.
    select(this.element).style('position', 'relative');
    const tooltip = select(this.element)
      .append('div')
      .attr('class', 'wg-tooltip')
      .style('position', 'absolute')
      .style('background', isDark ? 'rgba(30,41,59,0.92)' : 'rgba(255,255,255,0.9)')
      .style('padding', '4px 6px')
      .style('border-radius', '5px')
      .style('font-size', '12px')
      .style('color', isDark ? '#f1f5f9' : '#1f2937')
      .style('pointer-events', 'none')
      .style('z-index', '10')
      .style('opacity', 0);

    sections.forEach((sectionVar, sectionIdx) => {
      const sectionField = FIELDS[sectionVar] ||
        (sectionVar === 'cldfra' ? FIELDS.cldfra : field);
      const scaleBands = this.scaleForKey(sectionField.scaleKey);
      const { thresholds, labels } = centeredBandConfig(sectionVar, scaleBands);
      // Unit shown in the tooltip — lapse rate is °C/… but Skysight labels it °C.
      const sectionUnit = sectionVar === 'lapserate'
        ? '°C'
        : prettyUnitFor(sectionField.scaleKey, this.units);

      // Build the value grid + contour polygons for this section.
      const { values } = buildGrid(steps, sectionVar, this.units);
      if (sectionVar === 'temperature') {
        this.tempGrid = values;
      }
      const polygons = d3Contours()
        .size([cols, rows])
        .thresholds(thresholds)
        .smooth(true)(values);

      contoursGroup
        .selectAll(`.dataBand${sectionIdx}`)
        .data(
          polygons.map((contour, bandIdx) => {
            const label = labels[Math.min(bandIdx, labels.length - 1)] || null;
            // getFillColor transparent guard (bundle 1950): the lowest band of
            // the overlay (single-contour) section paints transparent.
            let fillColor;
            if (bandIdx === 0 && sectionIdx === 1) {
              fillColor = 'transparent';
            } else if (label) {
              fillColor = `rgb(${label.rgb.join(',')})`;
            } else {
              fillColor = getFillColor(contour, scaleBands);
            }
            const displayValue = label && label.value != null ? label.value : contour.value;
            return { contour, fillColor, bandIdx, displayValue };
          }),
        )
        .enter()
        .append('path')
        .attr('class', `dataBand${sectionIdx} contour-fill`)
        .attr('d', (d) => pathGen(d.contour) || '')
        .attr('fill', (d) => d.fillColor)
        .attr('transform', `scale(${gt}, 1)`)
        // Hide band 0 of the overlay section (bundle 1893).
        .style('display', (d) => (d.bandIdx === 0 && sectionIdx === 1 ? 'none' : ''))
        // Hover → show this band's value + unit at the cursor (bundle 1870-1880).
        .on('mousemove', (event, d) => {
          const [mx, my] = pointer(event, this.element);
          tooltip
            .style('opacity', 0.9)
            .style('left', `${mx + 10}px`)
            .style('top', `${my - 20}px`)
            .text(`${Math.round(d.displayValue * 100) / 100}${sectionUnit}`);
        })
        .on('mouseout', () => tooltip.style('opacity', 0));
    });

    // Zero-isotherm overlay on the Temperature tab (bundle 1897 / 1905).
    if (this.variable === 'temperature') {
      this.drawZeroIsotherm(gt);
    }

    // --- axes -----------------------------------------------------------------
    // x-axis lives at the margin (NOT inside wgContent) and is redrawn on zoom.
    // Pass plotHeight (reduced for the ti table band); the axes' vertical origin
    // is offset by TABLE_H so they line up with the shifted plot.
    this.drawAxes(svg, innerWidth, plotHeight, y, cols, TABLE_H);

    // --- barbs ----------------------------------------------------------------
    // Barbs go into the separate wg-barbs group (static wrapper, not scaled) so
    // glyphs keep their aspect ratio; the zoom handler repositions them by k.
    this.drawBarbs(wgBarbs, steps, contentWidth, y, cols, rows);
    this.updateBarbsVisibility();

    // --- overlays + icons (overview/cldcover only) ----------------------------
    if (isOverview) {
      const columns = cumulusColumns(steps);
      // Overlays bake their own translate(MARGIN_LEFT,MARGIN_TOP) into each path.
      // We append them to a sub-group of wgContent shifted by (-left,-top) so
      // that baked margin is cancelled (content origin already sits at the margin
      // via svgDraw). Icons take an explicit margin, so we pass {left:0,top:0}.
      const overlayG = wgContent
        .append('g')
        .attr('class', 'wg-overlays')
        .attr('transform', `translate(${-this.margin.left},${-this.margin.top})`);
      const zeroMargin = { left: 0, top: 0 };
      drawCloudArea(overlayG, steps, y, contentWidth, gt);
      drawBoundaryLine(overlayG, steps, y, contentWidth, gt);
      drawCondensationLine(overlayG, steps, y, contentWidth, gt);
      drawCumulus(wgContent, columns, steps, y, contentWidth, gt, zeroMargin);
      drawRain(wgContent, columns, steps, y, contentWidth, gt, zeroMargin, plotHeight);
      drawLightning(wgContent, columns, steps, y, contentWidth, gt, zeroMargin);
    }

    // --- day dividers + date labels (inside wgContent: move with zoom) ---------
    // Use plotHeight: wgContent is inside wgClip (shifted down by TABLE_H), so
    // its local y-origin is already at the top of the plot.
    this.drawDayDividers(wgContent, contentWidth, plotHeight, cols, steps);

    // --- layers legend (pinned top-left OUTSIDE wgContent) --------------------
    if (isOverview) {
      this.drawLayersLegend(svg);
    }

    // --- legend (m/s colour bar — OUTSIDE wgContent, fixed) -------------------
    const legendBands = centeredBandConfig(this.variable, this.scaleForKey(field.scaleKey));
    const legend = svg
      .append('g')
      .attr('id', 'wg-legend')
      .attr(
        'transform',
        // Flush the 25px swatch column against the right edge (margin.right == 25),
        // so it's fully visible like Skysight (was +8 → clipped past the edge).
        `translate(${this.margin.left + innerWidth},${this.margin.top + 20})`,
      );
    this.legend = legend;
    drawLegend(
      legend,
      legendBands,
      innerHeight,
      prettyUnitFor(field.scaleKey, this.units),
      this.legendTextColor,
    );

    // --- zoom -----------------------------------------------------------------
    this.attachZoom(svg, innerWidth, contentWidth, cols);
  }

  // Day dividers (dashed verticals) + per-day date labels, both inside wgContent
  // so they move/scale with zoom. Boundaries assume 13 steps/day: the day1|day2
  // and day2|day3 splits sit at column 12.5 and 25.5. Mirrors bundle 1567-1624.
  drawDayDividers(wgContent, contentWidth, innerHeight, cols, steps) {
    if (cols < 2) return;
    const denom = cols - 1;

    // Dashed dividers at the 12.5 / 25.5 column boundaries (only if in range).
    [12.5, 25.5].forEach((colBoundary, idx) => {
      if (colBoundary > denom) return;
      const x = (colBoundary / denom) * contentWidth;
      wgContent
        .append('line')
        .attr('class', `wg-day-divider wg-day-divider-${idx + 1}`)
        .attr('x1', x)
        .attr('x2', x)
        .attr('y1', 0)
        .attr('y2', innerHeight)
        .attr('stroke', '#cbd5e1')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '5,5')
        .style('pointer-events', 'none');
    });

    // Date labels: one per day, derived from the first step of each day (indices
    // 0, 13, 26). Skysight positions at width/6.6, *2.6, *5.2 (draw() 1601-1623).
    // Labels: use this.i18n.dayLabels (localized, from the panel) when provided;
    // otherwise fall back to the dt-derived English formatter.
    const labelXFactors = [1 / 6.6, 2.6 / 6.6, 5.2 / 6.6];
    const dayLabels = Array.isArray(this.i18n.dayLabels) ? this.i18n.dayLabels : null;
    const dateLabelColor = this.dateLabelColor || '#1f2937';
    [0, 13, 26].forEach((stepIdx, dayIdx) => {
      const step = steps[stepIdx];
      if (!step || !step.dt) return;
      // If localized labels were supplied, only render up to their length.
      const text =
        dayLabels && dayLabels[dayIdx] != null
          ? dayLabels[dayIdx]
          : dayLabels
          ? null
          : formatDtLabel(step.dt);
      if (text == null) return;
      wgContent
        .append('text')
        .attr('class', `wg-date-label wg-date-label-${dayIdx + 1}`)
        .attr('x', contentWidth * labelXFactors[dayIdx])
        .attr('y', innerHeight + this.margin.bottom - 8)
        .attr('text-anchor', 'middle')
        .style('font-family', 'sans-serif')
        .style('font-size', '14px')
        .style('font-weight', 'bold')
        .style('fill', dateLabelColor)
        .style('pointer-events', 'none')
        .text(text);
    });
  }

  // Top-left layers legend (bundle 1626-1628): pink Condensation Layer,
  // black Boundary Layer, gray Cloud Cover. Drawn only on the Overview tab.
  drawLayersLegend(svg) {
    const legendTextColor = this.legendTextColor || '#1f2937';
    const rows = [
      { color: '#F071B4', label: this.i18n.condensationLayer ?? 'Condensation Layer' },
      { color: '#000000', label: this.i18n.boundaryLayer ?? 'Boundary Layer' },
      { color: 'rgba(70,70,70,0.55)', label: this.i18n.cloudCover ?? 'Cloud Cover' },
    ];
    const g = svg
      .append('g')
      .attr('class', 'wg-layers-legend')
      .attr('transform', `translate(${this.margin.left + 10},${this.margin.top + 8})`)
      .style('pointer-events', 'none');
    rows.forEach((r, i) => {
      const row = g.append('g').attr('transform', `translate(0,${i * 18})`);
      row
        .append('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', 13)
        .attr('height', 13)
        .attr('rx', 2)
        .attr('fill', r.color);
      row
        .append('text')
        .attr('x', 19)
        .attr('y', 11)
        .attr('font-size', '12px')
        .attr('font-weight', 600)
        .attr('fill', legendTextColor)
        .text(r.label);
    });
  }

  // Cloud-cover table (ti tab only, bundle 938-1051). Three rows of per-timestep
  // cloud fractions painted as filled cells in the reserved top band. Cells live
  // in CONTENT coordinates (x = i*contentWidth/cols) so they align with the
  // chart columns and inherit wgContent's zoom transform. Row→field mapping:
  // High=cfrach (top), Med=cfracm, Low=cfracl (bottom). NOTE: Skysight's bundle
  // pairs them the OTHER way (top "High" label → cfracl low-cloud field) — a
  // label↔data inversion vs the standard l/m/h = low/mid/high naming and the
  // altitude-up axis. We intentionally use the correct, intuitive pairing here.
  drawCloudTable(wgTable, steps, contentWidth, cols, rowHeight) {
    const cellW = contentWidth / cols;
    // rowIndex 0 = top ("High") → cfrach, 1 = "Med" → cfracm, 2 = "Low" → cfracl.
    const rowKeys = ['cfrach', 'cfracm', 'cfracl'];
    // Skysight uses the RAW cfrac (0–100) as the rgba alpha (bundle: rgba(150,150,
    // 150, cfrac)); any cfrac ≥ 1% clamps to fully opaque solid gray. We composite
    // that gray over the clear-sky base (lightblue) rather than leaving it
    // transparent: a sub-1% trace over a transparent fill would show the dark page
    // background through on dark theme and read as a dark patch. Compositing keeps
    // the cell OPAQUE so a trace reads as "almost clear" (faint blue-gray) on any
    // theme — matching Skysight's faint-on-white look. v=0 → lightblue, v≥1 →
    // solid gray, 0<v<1 → blended.
    const CLEAR_RGB = [173, 216, 230]; // 'lightblue'
    const CLOUD_RGB = [150, 150, 150];
    const cellFill = (cfrac) => {
      const v = cfrac == null ? 0 : parseFloat(cfrac);
      if (!v) return 'lightblue';
      const a = Math.min(1, v);
      const c = CLOUD_RGB.map((g, i) => Math.round(g * a + CLEAR_RGB[i] * (1 - a)));
      return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
    };

    // Inter-cell gridline: transparent (lets the chart background show through
    // the cell gaps) so it matches any theme without a hardcoded colour.
    const strokeColor = this.bgColor;
    rowKeys.forEach((key, rowIdx) => {
      const rowG = wgTable
        .append('g')
        .attr('class', `wg-table-row wg-table-row-${rowIdx}`)
        .attr('transform', `translate(0,${rowIdx * rowHeight})`);
      rowG
        .selectAll('rect')
        .data(steps)
        .enter()
        .append('rect')
        .attr('x', (d, i) => (i * contentWidth) / cols)
        .attr('y', 0)
        .attr('width', cellW)
        .attr('height', rowHeight)
        .attr('stroke', strokeColor)
        .attr('stroke-width', 3)
        .attr('fill', (d) => cellFill(d[key]));
    });
  }

  // Row labels for the cloud-cover table, drawn in the STATIC left margin (NOT
  // zoomed): "High"/"Med"/"Low" top→bottom, vertically centred in each row.
  // i18n with English fallbacks (the panel may pass these later).
  drawCloudTableLabels(svgDraw, rowHeight) {
    const labelColor = this.tickLabelColor || 'rgba(0,0,0,0.6)';
    const labels = [
      this.i18n.high ?? 'High',
      this.i18n.mid ?? 'Med',
      this.i18n.low ?? 'Low',
    ];
    const g = svgDraw
      .append('g')
      .attr('class', 'wg-table-labels')
      .style('pointer-events', 'none');
    labels.forEach((text, rowIdx) => {
      g.append('text')
        .attr('x', -6)
        .attr('y', rowIdx * rowHeight + rowHeight / 2)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'central')
        .style('font-size', '10px')
        .style('fill', labelColor)
        .text(text);
    });
  }

  // x-axis (time-of-day) + y-axis (altitude) (bundle 1110-1124, 1234-1255).
  drawAxes(svg, innerWidth, plotHeight, y, cols, tableH = 0) {
    // X axis: index → pixel across the full contentWidth (so columns line up
    // with the zoomed content), label = (i % 13) + 8 hours-of-day. The axis is
    // clipped to the viewport via the wg-xaxis-clip path; on zoom it is rescaled
    // to range [0, contentWidth * k] (bundle x-axis 1110-1124 + updateSVG).
    svg
      .append('defs')
      .append('clipPath')
      .attr('id', 'wg-xaxis-clip')
      .append('rect')
      // Pad ±14px so the first ("8") and last ("20") hour labels — centered on
      // the plot edges — aren't clipped, while still hiding off-day ticks on zoom.
      .attr('x', -14)
      .attr('y', -2)
      .attr('width', innerWidth + 28)
      .attr('height', this.margin.bottom + 4);

    const xScale = scaleLinear()
      .domain([0, cols - 1])
      .range([0, this.contentWidth]);
    const xAxisG = svg
      .append('g')
      .attr('class', 'x-axis')
      .attr('clip-path', 'url(#wg-xaxis-clip)')
      .attr(
        'transform',
        `translate(${this.margin.left},${this.margin.top + tableH + plotHeight})`,
      )
      .call(
        axisBottom(xScale)
          .ticks(Math.max(1, cols - 1))
          .tickFormat((d) => `${(Number(d) % 13) + 8}`),
      );
    this.styleAxis(xAxisG);

    // Y axis: altitude ticks with unit suffix.
    const hgtLabel = UNIT_LABELS[this.units.hgt] || this.units.hgt || 'm';
    const yAxisG = svg
      .append('g')
      .attr('class', 'y-axis')
      .attr(
        'transform',
        `translate(${this.margin.left},${this.margin.top + tableH})`,
      )
      .call(
        axisLeft(y).tickFormat((d) => `${Math.round(d)}${hgtLabel}`),
      );
    this.styleAxis(yAxisG);

    // Terrain (ground) tick at the very bottom of the y-axis — the bottom of the
    // altitude domain, i.e. the projected base. Skysight labels this with the
    // TERRAIN value (its "O", bundle 1221/1256), NOT the raw MSL base: it shows
    // e.g. "535m" for a 627.9m base. It's only drawn when the terrain isn't a
    // round 1000 (else it would duplicate a normal gridline tick).
    const baseDisp = Math.round(this.terrain);
    if (baseDisp % 1000 !== 0) {
      yAxisG
        .append('text')
        .attr('class', 'wg-base-label')
        .attr('x', -9)
        .attr('y', plotHeight)
        .attr('dy', '0.32em')
        .attr('text-anchor', 'end')
        .attr('fill', this.tickLabelColor || 'rgba(0,0,0,0.6)')
        .style('font-size', '10px')
        .style('font-weight', 'bold')
        .text(`${baseDisp}${hgtLabel}`);
    }

    // No "Time (local)" axis title: the bottom margin now hosts the per-day date
    // labels (drawn inside wgContent), matching Skysight's layout.
  }

  // Apply theme-aware colours to a d3 axis <g>: tick TEXT, the domain path, and
  // the tick lines. Called after every axis (re)draw, including the x-axis
  // redraw in attachZoom. Reads this.tickLabelColor/this.axisLineColor (set in
  // draw()), with light-theme fallbacks if draw() hasn't populated them yet.
  styleAxis(axisG) {
    const tickLabelColor = this.tickLabelColor || 'rgba(0,0,0,0.6)';
    const axisLineColor = this.axisLineColor || 'rgba(0,0,0,0.2)';
    axisG.selectAll('text').attr('fill', tickLabelColor);
    axisG.select('path.domain').attr('stroke', axisLineColor);
    axisG.selectAll('.tick line').attr('stroke', axisLineColor);
  }

  // Wind barbs, one <g> per timestep, then a barb per altitude slice ≥ terrain.
  // Bundle 1274-1344: transform translate(0,y) rotate(atan2(-v,u)) scale(p(speed)),
  // path La(speed,{centered}). We use barbPath + barbRotation.
  // `contentWidth` is the full overscan canvas width: barb columns are spread
  // across it (and live inside wgContent, so they scale/scroll with zoom).
  drawBarbs(svgDraw, steps, contentWidth, y, cols, rows) {
    const altMeters = this.alt;
    const hgt = this.units.hgt;
    const maxAlt = this.maxAltitude;
    const sliceCount = steps[0].data.length;

    // barb scale factor by speed (bundle p / 1097-1102). Size from the VIEWPORT
    // width (not the 3.2× canvas) so glyphs aren't oversized.
    const vpWidth = this.innerWidth || contentWidth;
    const speedScale = scaleLinear()
      .domain([0, 50])
      .range([
        Math.max(0.9, Math.min(1, vpWidth / 1000)),
        Math.max(1, Math.min(1.7, vpWidth / 600)),
      ]);

    // terrain ceiling in metres (bundle O / 1221): below this, barbs are hidden.
    const terrainFraction = Math.max(
      0,
      ((this.base - this.loAlt) / (altMeters - this.loAlt)) * altMeters,
    );

    // Vertical thinning: Skysight does NOT draw a barb per 100 m slice — it
    // targets ~50 px spacing (bundle 1285-1340) so rows are sparse aloft and
    // denser near the boundary layer. Pick the row indices whose pixel
    // positions are ≥ MIN_BARB_GAP apart and above terrain. Altitude→y is
    // column-independent, so we compute the kept rows once and reuse per column.
    const MIN_BARB_GAP = 46;
    const altOf = (c) => (c * maxAlt) / (sliceCount - 1);
    const keptRows = [];
    let lastPy = Infinity;
    for (let c = 0; c < sliceCount; c++) {
      const altOfRow = altOf(c);
      if (altOfRow < terrainFraction) continue;
      const py = y(convertUnit(altOfRow, hgt));
      if (lastPy - py >= MIN_BARB_GAP) {
        keptRows.push(c);
        lastPy = py;
      }
    }

    const barbGroups = svgDraw
      .selectAll('.barbs')
      .data(steps)
      .enter()
      .append('g')
      .attr('class', 'barbs')
      .attr('transform', (d, i) => `translate(${(i * contentWidth) / cols}, 0)`);

    barbGroups
      .selectAll('.altitude')
      .data((d) => keptRows.map((c) => ({ slice: d.data[c], c })))
      .enter()
      .append('path')
      .attr('stroke', '#000000')
      .attr('fill', '#000000')
      .attr('transform', ({ slice, c }) => {
        // altOf(c): actual altitude (c=0 = lowest), per bundle line 1316-1317
        //   ot = (C * this.maxAltitude) / (s[0].data.length - 1); N = x(B(ot, hgt))
        const py = y(convertUnit(altOf(c), hgt));
        const u = parseFloat(slice.u);
        const v = parseFloat(slice.v);
        const speedMs = Math.hypot(u, v);
        const rot = barbRotation(u, v);
        return `translate(0, ${py}) rotate(${rot}) scale(${speedScale(speedMs)})`;
      })
      .attr('d', ({ slice }) => {
        const speedMs = Math.hypot(parseFloat(slice.u), parseFloat(slice.v));
        const speedKt = convertUnit(speedMs, 'kts');
        return barbPath(speedKt, { centered: true });
      });
  }

  // Zero-isotherm marching-squares line on the Temperature tab (bundle 1905).
  drawZeroIsotherm(gt) {
    if (!this.tempGrid || this.tempGrid.length === 0) return;
    const threshold = convertUnit(0, this.units.temp);
    const segments = isolines(
      this.tempGrid,
      this.contourGridWidth,
      this.contourGridHeight,
      threshold,
    );
    if (segments.length === 0) return;

    const lineStrings = segments.map((seg) => ({
      type: 'LineString',
      coordinates: seg.map((p) => [p.x, p.y]),
    }));
    const pathGen = geoPath(this.projection);

    const g = this.contoursGroup
      .append('g')
      .attr('class', 'zero-isotherm')
      .attr('transform', `scale(${gt}, 1)`)
      .style('pointer-events', 'none');

    // White halo underneath.
    g.selectAll('.zero-isotherm-outline')
      .data(lineStrings)
      .enter()
      .append('path')
      .attr('class', 'zero-isotherm-outline')
      .attr('d', (d) => pathGen(d) || '')
      .attr('fill', 'none')
      .attr('stroke', 'rgba(255, 255, 255, 0.9)')
      .attr('stroke-width', 3)
      .attr('stroke-linecap', 'round')
      .attr('stroke-linejoin', 'round');

    // Blue line on top.
    g.selectAll('.zero-isotherm-line')
      .data(lineStrings)
      .enter()
      .append('path')
      .attr('class', 'zero-isotherm-line')
      .attr('d', (d) => pathGen(d) || '')
      .attr('fill', 'none')
      .attr('stroke', '#0f5fff')
      .attr('stroke-width', 1.5)
      .attr('stroke-linecap', 'round')
      .attr('stroke-linejoin', 'round');
  }

  // d3-zoom: scaleExtent([1/3, 1]). The whole wg-content group (contours, barbs,
  // overlays, icons, dividers, date labels) is transformed by
  // `translate(tx,0) scale(k,1)`; the x-axis (outside wgContent) is redrawn with
  // range [0, contentWidth*k]. Default transform k=1, tx=0 → shows day 1 (the
  // left innerWidth of the contentWidth). Zooming out (k→1/3) fits ~3 days.
  // Mirrors Skysight updateSVG 1681-1768 + scaleExtent 1679 + clip overscan.
  attachZoom(svg, innerWidth, contentWidth, cols) {
    const self = this;

    // Clamp the pan offset so the content edge can't pull inside the viewport:
    // at zoom k the scaled content is contentWidth*k wide, so tx ∈
    // [innerWidth - contentWidth*k, 0].
    const clampTx = (tx, k) => {
      const minTx = Math.min(0, innerWidth - contentWidth * k);
      return Math.max(minTx, Math.min(0, tx));
    };

    const applyTransform = (tx, k) => {
      self.transformK = k;
      const ctx = clampTx(tx, k);
      if (self.wgContent) {
        self.wgContent.attr('transform', `translate(${ctx},0) scale(${k},1)`);
      }
      // The cloud-cover table (ti tab) shares the SAME horizontal zoom transform
      // as wgContent so its cells stay aligned with the chart columns.
      if (self.wgTable) {
        self.wgTable.attr('transform', `translate(${ctx},0) scale(${k},1)`);
      }
      // Redraw x-axis: range scaled by k, then translated by ctx so ticks track
      // the columns (the axis g is clipped to the viewport).
      const xScaleZoomed = scaleLinear()
        .domain([0, cols - 1])
        .range([ctx, ctx + contentWidth * k]);
      const xAxisG = svg
        .select('g.x-axis')
        .call(
          axisBottom(xScaleZoomed)
            .ticks(Math.max(1, cols - 1))
            .tickFormat((d) => `${(Number(d) % 13) + 8}`),
        );
      self.styleAxis(xAxisG);

      // Reposition barb groups by the zoom factor WITHOUT scaling the glyphs
      // (they live in the static wg-barbs group, not the scaled wgContent). Base
      // x is (i*contentWidth/cols) in content coords; mirror wgContent's
      // translate(ctx)+scale(k) so barbs stay aligned with the contours. At k=1,
      // ctx=0 this equals the original positions.
      if (self.wgBarbs && self.cols) {
        const cw = self.contentWidth;
        const nCols = self.cols;
        self.wgBarbs
          .selectAll('.barbs')
          .attr(
            'transform',
            (d, i) => `translate(${((i * cw) / nCols) * k + ctx}, 0)`,
          );
      }
    };

    const zoomBehavior = d3Zoom()
      .scaleExtent([1 / 3, 1])
      // translateExtent in zoom's own coordinate space; combined with the clamp
      // above it prevents panning past either content edge.
      .translateExtent([[0, 0], [contentWidth, this.height]])
      .extent([[0, 0], [innerWidth, this.height]])
      .on('zoom', (event) => {
        const t = event && event.transform ? event.transform : null;
        const k = (t ? t.k : 1) || 1;
        const tx = t ? t.x : 0;
        applyTransform(tx, k);
      });

    this.zoomBehavior = zoomBehavior;
    svg.call(zoomBehavior);
    // Default transform: k=1, tx=0 (day 1 in view).
    applyTransform(0, 1);
  }
}

// Format a 'YYYYMMDDHHmm' UTC stamp as a short "Sat 7 Jun" label (i18n-free).
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
function formatDtLabel(dt) {
  if (!dt || String(dt).length < 8) return '';
  const s = String(dt);
  const year = parseInt(s.slice(0, 4), 10);
  const month = parseInt(s.slice(4, 6), 10) - 1;
  const day = parseInt(s.slice(6, 8), 10);
  // Construct in UTC so the weekday matches the UTC date in the stamp.
  const d = new Date(Date.UTC(year, month, day));
  if (Number.isNaN(d.getTime())) return '';
  return `${WEEKDAYS[d.getUTCDay()]} ${day} ${MONTHS[month]}`;
}

export default WindgramRenderer;
